/* eslint-disable import/no-cycle */
import {
  SubmissionPollResolution,
  SubmissionStatusRecord,
  resolveSubmissionPollingState,
} from '../../util/submissionState';
import { ActionType, State } from '.';

const RECEIVE_SUBMISSION_STATUS = 'RECEIVE_SUBMISSION_STATUS';
const SET_SUBMISSION_POLL_GENERATION = 'SET_SUBMISSION_POLL_GENERATION';
const INVALIDATE_SUBMISSION_STATUS = 'INVALIDATE_SUBMISSION_STATUS';
const CLEAR_SUBMISSION_STATUSES = 'CLEAR_SUBMISSION_STATUSES';

export type SubmissionStoreRecord = SubmissionStatusRecord & {
  caseExternalId: string;
  pollGeneration: number;
};

export type SubmissionState = {
  byCaseExternalId: Record<string, SubmissionStoreRecord>;
};

type ReceiveSubmissionStatusAction = {
  type: typeof RECEIVE_SUBMISSION_STATUS;
  payload: SubmissionStoreRecord;
};

type SetSubmissionPollGenerationAction = {
  type: typeof SET_SUBMISSION_POLL_GENERATION;
  payload: {
    caseExternalId: string;
    pollGeneration: number;
    submissionId?: string;
  };
};

type InvalidateSubmissionStatusAction = {
  type: typeof INVALIDATE_SUBMISSION_STATUS;
  payload: string;
};

type ClearSubmissionStatusesAction = {
  type: typeof CLEAR_SUBMISSION_STATUSES;
};

export type SubmissionActionType =
  | ReceiveSubmissionStatusAction
  | SetSubmissionPollGenerationAction
  | InvalidateSubmissionStatusAction
  | ClearSubmissionStatusesAction;

const initialState: SubmissionState = {
  byCaseExternalId: {},
};

export const receiveSubmissionStatus = (
  caseExternalId: string,
  statusRecord: SubmissionStatusRecord,
  pollGeneration = 0
): ReceiveSubmissionStatusAction => ({
  type: RECEIVE_SUBMISSION_STATUS,
  payload: {
    caseExternalId,
    pollGeneration,
    ...statusRecord,
  },
});

export const setSubmissionPollGeneration = (
  caseExternalId: string,
  pollGeneration: number,
  submissionId?: string
): SetSubmissionPollGenerationAction => ({
  type: SET_SUBMISSION_POLL_GENERATION,
  payload: { caseExternalId, pollGeneration, submissionId },
});

export const invalidateSubmissionStatus = (
  caseExternalId: string
): InvalidateSubmissionStatusAction => ({
  type: INVALIDATE_SUBMISSION_STATUS,
  payload: caseExternalId,
});

export const clearSubmissionStatuses = (): ClearSubmissionStatusesAction => ({
  type: CLEAR_SUBMISSION_STATUSES,
});

export default (state: SubmissionState = initialState, action: ActionType): SubmissionState => {
  switch (action.type) {
    case RECEIVE_SUBMISSION_STATUS: {
      const currentSubmission = state.byCaseExternalId[action.payload.caseExternalId];
      if (currentSubmission && currentSubmission.pollGeneration !== action.payload.pollGeneration) {
        return state;
      }
      if (
        currentSubmission?.pollGeneration > 0 &&
        action.payload.pollGeneration > 0 &&
        currentSubmission.submissionId &&
        action.payload.submissionId &&
        currentSubmission.submissionId !== action.payload.submissionId
      ) {
        return state;
      }

      return {
        ...state,
        byCaseExternalId: {
          ...state.byCaseExternalId,
          [action.payload.caseExternalId]: action.payload,
        },
      };
    }
    case SET_SUBMISSION_POLL_GENERATION: {
      const currentSubmission = state.byCaseExternalId[action.payload.caseExternalId];
      if (currentSubmission && currentSubmission.pollGeneration > action.payload.pollGeneration) {
        return state;
      }

      // startPolling advances the generation for a brand-new submissionId, so a
      // previous submission's terminal snapshot (status, submissionId, etc.) must
      // not carry forward onto it — otherwise the identity guard above rejects the
      // new submission's own accept/poll dispatches forever, and the store keeps
      // reporting the OLD submission's terminal state while the orchestrator polls
      // the new one. stopPolling (no submissionId passed) still preserves the
      // snapshot, which is what "advances poll generation without discarding the
      // current snapshot" covers.
      //
      // Deliberately just two conjuncts, not three: we do NOT also require
      // `currentSubmission?.submissionId` to be truthy. If the stored record has no
      // submissionId of its own (e.g. it was already wiped by a prior generation
      // bump), we still can't be sure it belongs to the submission now starting, so
      // the safe default is to drop it rather than let unowned data carry forward.
      // `currentSubmission?.submissionId` (not a bare property access) is required
      // to stay crash-safe when there is no current record at all, i.e. the first
      // submission on a case — a bare access leaves this duck's own suite green,
      // since every fixture here has a record; only the orchestrator's end-to-end
      // "resubmitting the same case" test starts from a truly empty state and would
      // crash on a bare access, so that is the test guarding this chain.
      const isDifferentSubmission =
        !!action.payload.submissionId &&
        currentSubmission?.submissionId !== action.payload.submissionId;

      return {
        ...state,
        byCaseExternalId: {
          ...state.byCaseExternalId,
          [action.payload.caseExternalId]: {
            ...(isDifferentSubmission ? undefined : currentSubmission),
            caseExternalId: action.payload.caseExternalId,
            pollGeneration: action.payload.pollGeneration,
          },
        },
      };
    }
    case INVALIDATE_SUBMISSION_STATUS: {
      const byCaseExternalId = { ...state.byCaseExternalId };
      delete byCaseExternalId[action.payload];
      return { ...state, byCaseExternalId };
    }
    case CLEAR_SUBMISSION_STATUSES:
      return initialState;
    default:
      return state;
  }
};

export const getSubmissionStatusPayload = (
  state: State,
  caseExternalId?: string | null
): SubmissionStoreRecord | undefined => {
  if (!caseExternalId) {
    return undefined;
  }

  return state.submission.byCaseExternalId[caseExternalId];
};

export const getSubmissionPollResolution = (
  state: State,
  caseExternalId?: string | null
): SubmissionPollResolution =>
  resolveSubmissionPollingState(getSubmissionStatusPayload(state, caseExternalId));
